// PWA glue: service-worker registration, standalone navigation, and last-visit
// recording. Normal browser use never changes the PWA resume destination.
//
//   1. A floating Back button. In standalone mode there is no browser back
//      button or URL bar, so without this a user who taps into a course lesson
//      has no reliable way back. Shown only when running as an installed app,
//      when there is somewhere to go back to, and when the bottom tab bar is
//      not already on screen (the tabs are the way around the main app).
//
//   2. "Maintain last visit" — send stable standalone URLs to the server, whose
//      signed cookie lets /pwa-launch redirect before any application page is
//      painted. localStorage remains as a compatibility bridge for installations
//      whose cached manifest still launches the old /videos start URL.

import {
  LAST_VISIT_KEY,
  canonicalVisitPath,
  readStoredVisit
} from './pwa_last_visit'

const LEGACY_START_PATHS = ['/', '/videos']
const REMEMBER_VISIT_URL = '/pwa/last-visit'
const REMEMBER_DEBOUNCE_MS = 100

let pendingPath = null
let rememberTimer = null
let lastSentPath = null
let lastSentAt = 0

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {
      // Registration failures are non-fatal — the app works without the SW.
    });
  });
}

// The tab bar is the primary way around the installed app. The floating
// control only earns its place on screens that do not render it (course
// player) or where it is hidden (desktop width). Keyboard dismissal uses
// visibility, not display, so a tab screen stays "visible" while typing.
// Onboarding owns its navigation entirely, so the generic control must never
// appear anywhere within that flow.
function tabBarVisible() {
  const bar = document.getElementById('tab_bar');
  if (!bar) return false;
  return getComputedStyle(bar).display !== 'none';
}

function isOnboardingPath(path) {
  return path === '/onboarding' || path.startsWith('/onboarding/');
}

function ensureBackButton() {
  if (!isStandalone()) return;
  let button = document.getElementById('pwa-back-button');

  // Turbo keeps the document (and therefore this injected button) between
  // visits. Hide an existing control as well as avoiding creation on a cold
  // onboarding load.
  if (isOnboardingPath(window.location.pathname)) {
    if (button) button.style.display = 'none';
    return;
  }

  if (!button) {
    button = document.createElement('button');
    button.id = 'pwa-back-button';
    button.type = 'button';
    button.setAttribute('aria-label', 'Go back');
    button.innerHTML =
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M15 18l-6-6 6-6"/></svg>';
    Object.assign(button.style, {
      position: 'fixed',
      left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      zIndex: '40',
      width: '44px',
      height: '44px',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '9999px',
      border: '1px solid rgba(255,255,255,0.14)',
      background: 'rgba(20,22,24,0.72)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#EAE6DF',
      boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
      cursor: 'pointer'
    });
    button.addEventListener('click', () => window.history.back());
    document.body.appendChild(button);
  }
  const show = window.history.length > 1 && !tabBarVisible();
  button.style.display = show ? 'inline-flex' : 'none';
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

function persistPendingVisit(force = false) {
  if (rememberTimer) {
    clearTimeout(rememberTimer)
    rememberTimer = null
  }

  const path = pendingPath
  pendingPath = null
  if (!path) return

  const now = Date.now()
  if (!force && path === lastSentPath) return
  // visibilitychange and pagehide can fire back-to-back on mobile.
  if (force && path === lastSentPath && now - lastSentAt < 1000) return

  const token = csrfToken()
  if (!token) return

  lastSentPath = path
  lastSentAt = now
  window.fetch(REMEMBER_VISIT_URL, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token
    },
    body: JSON.stringify({ path })
  }).catch(() => {
    // localStorage still supports the legacy launch path. A later event retries
    // the signed-cookie write without affecting the current navigation.
    if (lastSentPath === path) lastSentPath = null
  })
}

function rememberVisit({ immediate = false, force = false } = {}) {
  if (!isStandalone()) return

  const path = canonicalVisitPath(window.location)
  if (!path) return

  const now = Date.now()
  try {
    window.localStorage.setItem(LAST_VISIT_KEY, JSON.stringify({ path, ts: now }))
  } catch (error) { /* private mode */ }

  pendingPath = path
  if (immediate) {
    persistPendingVisit(force)
  } else {
    if (rememberTimer) clearTimeout(rememberTimer)
    rememberTimer = setTimeout(() => persistPendingVisit(force), REMEMBER_DEBOUNCE_MS)
  }
}

// Existing installations can retain the old /videos start URL until the browser
// refreshes their manifest. Keep one pre-server fallback so those users migrate
// their saved localStorage entry into the signed cookie instead of losing it.
function maybeRestoreLegacyLastVisit() {
  if (!isStandalone()) return
  if (!LEGACY_START_PATHS.includes(window.location.pathname)) return
  if (document.referrer) return

  const last = readStoredVisit(window.localStorage)
  if (last && last !== window.location.pathname) window.location.replace(last)
}

registerServiceWorker();
maybeRestoreLegacyLastVisit();

document.addEventListener('turbo:load', () => {
  ensureBackButton();
  rememberVisit();
});

window.addEventListener('popstate', () => rememberVisit())
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') rememberVisit({ immediate: true, force: true })
})
window.addEventListener('pagehide', () => rememberVisit({ immediate: true, force: true }))

// Specs call this directly. A synthetic turbo:load would also run page-view tracking.
window.rafaEnsureBackButton = ensureBackButton;
window.rafaRememberVisit = rememberVisit;

// Cover the very first paint before Turbo has fired (and non-Turbo layouts).
if (document.readyState !== 'loading') {
  ensureBackButton();
  rememberVisit();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    ensureBackButton();
    rememberVisit();
  });
}
